import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import useForm from 'assets/components/form/hooks/Form';
import Icon from 'assets/components/icon/Icon';
import TextInput from 'assets/components/inputs/text/TextInput';
import ErrorWrapper from 'assets/components/inputs/wrappers/ErrorWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import Confirm from 'assets/components/page/templates/Confirm';
import ThemeConfirmDefault from 'assets/components/page/templates/themes/Theme.Confirm.Default';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { defaultDateFormat, localizeDate } from 'assets/utils/data/Date';
import Contract from 'models/sales/contract/Model.Contract';
import {
  canCreateClaimForContractStatus,
  isClaimContractExpired,
  isClaimContractExpiredWindowExceeded,
} from 'module/claims/helper/Claim.ContractEligibility';
import { usePublicClaimPageRouter } from 'module/public-web/views/claimSubmit/Claim.Submit.Hooks';
import { useState } from 'react';

type ContractSearchForm = {
  contractNumber: string;
  lastName: string;
  vin: string;
};

export default function ClaimContractSearchForm(props: { onSubmit: (contract: Model.IContract) => void }) {
  const { lang, translate } = getLocales();
  const pageRouter = usePublicClaimPageRouter();
  const [contractForClaim, setContractForClaim] = useState<Contract>(undefined);

  const form = useForm<ContractSearchForm>({
    default: { ...pageRouter.query },
    validation: (data, errors) => {
      if (!data.vin) errors.vin = lang.required;
      else if (data.vin.length < 8) errors.vin = lang.vinMustBeAtLeast8CharsLong;
      else if (!data.contractNumber && !data.lastName) {
        errors.lastName = lang.required;
        errors.contractNumber = lang.required;
      }
    },
    onSubmit: (data) => {
      listPublicApi.execute(data);
    },
  });

  const listPublicApi = useApi({
    action: Contract.listPublic,
    wait: true,
    callback: () => {
      form.set({ ...form.data });
    },
  });

  const handleSelect = (contract: Contract) => {
    if (!canCreateClaimForContractStatus(contract) || isClaimContractExpiredWindowExceeded(contract)) return;

    const isExpired = isClaimContractExpired(contract);
    if (isExpired) setContractForClaim(contract);
    else props.onSubmit(contract);
  };

  return (
    <>
      {contractForClaim && (
        <Confirm
          data={contractForClaim}
          onConfirm={props.onSubmit}
          onClose={() => setContractForClaim(undefined)}
          actionName={lang.expiredClaimCreationWarning}
          itemName={contractForClaim.displayInfo.title}
          confirmationType="nextButton"
          subcomponents={ThemeConfirmDefault}
        />
      )}
      <h2>{lang.contract}</h2>
      <InputWrapper>
        <TextInput
          name="vin"
          label={lang.vin}
          value={form.data.vin}
          error={form.errors.vin}
          onChange={form.update}
          icon={<Icon class="fas-car" />}
        />
        <TextInput
          name="contractNumber"
          label={lang.contractNumber}
          value={form.data.contractNumber}
          error={form.errors.contractNumber}
          onChange={form.update}
          icon={<Icon class="fas-file-contract" />}
        />
        <TextInput
          name="lastName"
          label={lang.lastName}
          value={form.data.lastName}
          error={form.errors.lastName}
          onChange={form.update}
          icon={<Icon class="if-quill-pen" />}
        />
      </InputWrapper>
      {!listPublicApi.payload?.data.length ? (
        <>
          <ErrorWrapper>{listPublicApi.message || lang.noInfo}</ErrorWrapper>
          <br />
        </>
      ) : (
        <>
          <table className={ThemeTableSimple}>
            <thead>
              <tr>
                <th children={lang.contractNumber} />
                <th children={lang.status} />
                <th children={lang.productName} />
                <th children={lang.clientName} />
                <th children={lang.vin} />
                <th children={lang.expiryDate} />
                <th children={lang.dealer} />
                <th children={lang.actions} style={{ textAlign: 'center' }} />
              </tr>
            </thead>
            <tbody>
              <DataMap
                data={listPublicApi.payload?.data}
                render={({ data }) => {
                  const hasAllowedStatus = canCreateClaimForContractStatus(data);
                  const isExpiredWindowExceeded = isClaimContractExpiredWindowExceeded(data);
                  const isExpired = isClaimContractExpired(data);
                  const isSelectDisabled = isExpiredWindowExceeded || !hasAllowedStatus;

                  return (
                    <tr key={data.id ?? data.legacyId ?? data.appNumber}>
                      <td children={data.appNumber} />
                      <td
                        style={{
                          color: !hasAllowedStatus ? Theme.Color.error : undefined,
                        }}
                        children={data.statusCode ? translate(data.statusCode) : lang.noStatusDefined}
                      />
                      <td children={data.legacyProductName ?? data.product?.name} />
                      <td children={data.client?.displayInfo?.title} />
                      <td children={data.vehicle?.vin} />
                      <td
                        style={{
                          color: isExpired ? Theme.Color.error : undefined,
                        }}
                        children={localizeDate(data.expiryDate, defaultDateFormat)}
                      />
                      <td children={data.legacyDealerName ?? data.dealer?.name} />

                      <td style={{ textAlign: 'center' }}>
                        <Button
                          render={lang.select}
                          class={ThemeButtonCircle}
                          htmlElementProps={{
                            title: isExpiredWindowExceeded
                              ? lang.expiredContractClaimWindowExceeded
                              : lang.select,
                          }}
                          style={{ display: 'inline-block' }}
                          disabled={isSelectDisabled}
                          onClick={handleSelect}
                          data={data}
                        />
                      </td>
                    </tr>
                  );
                }}
              />
            </tbody>
          </table>
          <br />
        </>
      )}
      <Button
        onClick={form.submit}
        media={<Icon class="fas-search" />}
        render={lang.search}
        active={!form.hasErrors}
        disabled={form.hasErrors}
        isLoading={form.isSubmitted}
        class={ThemeButtonCircle}
        htmlElementProps={{ title: lang.search }}
      />
    </>
  );
}
